import type { AuthChangeEvent } from '@supabase/supabase-js'

export type AuthTransitionInput = {
  event: AuthChangeEvent
  previousUserId: string | null
  nextUserId: string | null
  hasLoadedProfile: boolean
  hasProcessedInitialSession: boolean
}

export type AuthTransitionDecision = {
  preserveProfile: boolean
  refreshProfile: boolean
  refreshServerTree: boolean
  recordLogin: boolean
  clearPrivateState: boolean
}

const NO_CHANGE: AuthTransitionDecision = {
  preserveProfile: false,
  refreshProfile: false,
  refreshServerTree: false,
  recordLogin: false,
  clearPrivateState: false
}

export function resolveAuthTransition({
  event,
  previousUserId,
  nextUserId,
  hasLoadedProfile,
  hasProcessedInitialSession
}: AuthTransitionInput): AuthTransitionDecision {
  if (event === 'INITIAL_SESSION') {
    return {
      ...NO_CHANGE,
      preserveProfile: previousUserId === nextUserId && nextUserId !== null && hasLoadedProfile
    }
  }

  if (event === 'SIGNED_OUT') {
    return {
      ...NO_CHANGE,
      refreshServerTree: true,
      clearPrivateState: true
    }
  }

  if (!nextUserId) {
    const lostExistingIdentity = previousUserId !== null
    return {
      ...NO_CHANGE,
      refreshServerTree: lostExistingIdentity,
      clearPrivateState: lostExistingIdentity
    }
  }

  const sameUser = previousUserId === nextUserId
  const differentUser = previousUserId !== null && !sameUser
  const profileUpdated = event === 'USER_UPDATED'

  return {
    preserveProfile: sameUser && hasLoadedProfile && !profileUpdated,
    refreshProfile: profileUpdated,
    refreshServerTree: differentUser,
    recordLogin: event === 'SIGNED_IN' && hasProcessedInitialSession && !sameUser,
    clearPrivateState: differentUser
  }
}
